import { useMemo, useState } from 'react';
import type { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { ProcessingNotice } from '@/components/ui/processing-notice';
import { downloadPropertyReport, PropertySummary, reconcileProperty } from '../../api/reconToolsApi';
import { downloadBlob } from '../../api/files';
import { Pill, RecordTable, Section, Stat } from './reconUi';

const STEPS = [
  "Upload StayLedger's Master Booking Register (your own system's export).",
  'Upload the OTA exports you have: Airbnb, Booking.com, MakeMyTrip and Agoda. Each file type is detected from its columns.',
  'Click Detect & Run Reconciliation. Bookings are matched by external booking ID.',
  'Download the full multi-tab Excel report.',
];

type Tier = 'ok' | 'warn' | 'error' | 'info';
const RESULT_META: Record<string, { tier: Tier; label: string }> = {
  MATCHED: { tier: 'ok', label: 'No discrepancy.' },
  PENDING_PAYOUT: { tier: 'info', label: "OTA hasn't paid out yet (informational, not an error)." },
  MISSING_IN_STAYLEDGER: { tier: 'error', label: "OTA has the booking; StayLedger doesn't." },
  MISSING_IN_OTA_REPORT: { tier: 'error', label: "StayLedger has the booking; the OTA export doesn't." },
  DUPLICATE_IN_STAYLEDGER: { tier: 'warn', label: 'Booking logged more than once internally.' },
  STATUS_MISMATCH: { tier: 'warn', label: 'e.g. one side shows Cancelled, the other Confirmed.' },
  GROSS_AMOUNT_MISMATCH: { tier: 'error', label: 'Booking value disagrees (active bookings only).' },
  COMMISSION_VARIANCE: { tier: 'error', label: 'Commission alone explains the payout gap.' },
  TAX_VARIANCE: { tier: 'error', label: 'Tax/deductions alone explain the payout gap.' },
  COMMISSION_AND_TAX_VARIANCE: { tier: 'error', label: 'Both commission and tax are off.' },
  PAYOUT_VARIANCE_FROM_EXPECTED: { tier: 'error', label: 'Payout is off but commission and tax both check out: needs manual review.' },
};

const MAX_ROWS = 500;

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function PropertyReconPanel({ mission, onBack }: PanelProps) {
  const [register, setRegister] = useState<File[]>([]);
  const [ota, setOta] = useState<File[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PropertySummary | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [search, setSearch] = useState('');

  const files = [...register, ...ota];
  const ready = register.length > 0 && ota.length > 0;

  const fail = (message: string) => {
    setError(message);
    setStatus('error');
  };

  const handleRun = async () => {
    if (!ready) {
      fail("Upload StayLedger's register plus at least one OTA export.");
      return;
    }
    setStatus('loading');
    setError(null);
    setSummary(null);
    try {
      const data = await reconcileProperty(files);
      if (!data.can_run) {
        fail(
          !data.has_stayledger
            ? "StayLedger's Master Booking Register was not recognised in the uploaded files."
            : 'None of the uploaded OTA files were recognised. Check the detected files below.'
        );
      } else {
        setStatus('success');
      }
      setSummary(data);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Reconciliation failed.');
    }
  };

  const handleDownload = async () => {
    if (!ready) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadPropertyReport(files);
      downloadBlob(blob, filename);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Failed to build the report.');
    } finally {
      setDownloading(false);
    }
  };

  const visibleRows = useMemo(() => {
    if (!summary) return [];
    const q = search.trim().toLowerCase();
    return summary.rows.filter((r) => {
      const result = String(r.Match_Result ?? '');
      if (attentionOnly && (result === 'MATCHED' || result === 'PENDING_PAYOUT')) return false;
      return !q || Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(q));
    });
  }, [summary, attentionOnly, search]);

  const fileProps = { accept: mission.accept, hint: mission.maxSizeLabel, accentColor: mission.color };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide backLabel="Reconciliation Tools">
      <FileDrop label="StayLedger booking register (own system)" required {...fileProps} files={register} onFiles={setRegister} />
      <FileDrop
        label="OTA report(s): Airbnb / Booking.com / MakeMyTrip / Agoda"
        note="Select several at once. Related reports (e.g. Booking.com reservations + bank transfer) are linked automatically."
        required
        multiple
        {...fileProps}
        files={ota}
        onFiles={setOta}
      />

      <div className="flex gap-3">
        <Button
          onClick={handleRun}
          disabled={status === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="flex-1 text-white hover:opacity-90"
        >
          {status === 'loading' ? 'Reconciling…' : 'Detect & Run Reconciliation'}
        </Button>
        <Button variant="outline" onClick={handleDownload} disabled={!ready || downloading} className="flex-1">
          {downloading ? 'Preparing report…' : 'Download Full Report (.xlsx)'}
        </Button>
      </div>
      {status === 'loading' && <ProcessingNotice label="Reconciling…" />}
      {error && <ErrorNotice message={error} />}
      {status === 'idle' && !error && (
        <InfoNotice>📁 Upload StayLedger's register plus at least one OTA export to begin.</InfoNotice>
      )}

      {summary && (
        <div className="mt-4 space-y-4 text-sm">
          <Section title="Detected files">
            <div className="grid gap-2 sm:grid-cols-2">
              {summary.slot_order.map((key) => (
                <div key={key} className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-xs">
                  <span className="font-medium text-foreground">{summary.labels[key] ?? key}</span>
                  <span className={summary.detected[key] ? 'text-green-700' : 'text-muted-foreground'}>
                    {summary.detected[key] ?? 'not provided'}
                  </span>
                </div>
              ))}
            </div>
            {summary.unrecognized.length > 0 && (
              <p className="mt-2 text-xs text-amber-700">Not recognised (ignored): {summary.unrecognized.join(', ')}</p>
            )}
          </Section>

          {summary.can_run && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat label="Bookings compared" value={summary.summary.total} />
                <Stat label="Matched" value={summary.summary.matched} />
                <Stat label="Pending payout" value={summary.summary.pending} />
                <Stat label="Needs attention" value={summary.summary.attention} tone={summary.summary.attention > 0 ? 'bad' : undefined} />
              </div>

              <Section title="Breakdown by result type">
                <div className="overflow-x-auto rounded-md border border-border bg-background">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="px-3 py-1.5 font-semibold">Result type</th>
                        <th className="px-3 py-1.5 font-semibold">Count</th>
                        <th className="px-3 py-1.5 font-semibold">What it means</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_result.map((r) => (
                        <tr key={r.result} className="border-t border-border">
                          <td className="px-3 py-1.5">
                            <Pill value={r.result} tier={RESULT_META[r.result]?.tier} />
                          </td>
                          <td className="px-3 py-1.5">{r.count}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{RESULT_META[r.result]?.label ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="By platform">
                <RecordTable
                  rows={summary.by_platform.map((p) => ({
                    Platform: p.platform,
                    Total: p.total,
                    Matched: p.matched,
                    'Pending payout': p.pending,
                    'Issues flagged': p.issues,
                  }))}
                />
              </Section>

              <Section title={`Reconciliation detail (${visibleRows.length}${visibleRows.length > MAX_ROWS ? `, first ${MAX_ROWS} shown` : ''})`}>
                <div className="mb-2 flex flex-wrap items-center gap-4">
                  <input
                    className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                    placeholder="Search bookings…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={attentionOnly} onChange={(e) => setAttentionOnly(e.target.checked)} />
                    Needs attention only
                  </label>
                </div>
                <RecordTable rows={visibleRows} statusCols={['Match_Result']} max={MAX_ROWS} />
              </Section>
            </>
          )}
        </div>
      )}
    </MissionShell>
  );
}

export default PropertyReconPanel;
